import type { Branded } from '@/utils/typing/branded.type';
import type { LiteralValues } from '@/utils/typing/literal-values.type';
import { PrettyFeelSharedUiColorName, SharedUiColorNameV2 } from './shared-ui-colors.types';

export const PaletteVariant = {
    1: '1',
    2: '2',
    3: '3',
    4: '4',
    5: '5',
    6: '6',
    7: '7',
    8: '8',
    9: '9',
    10: '10',
    11: '11',
    12: '12'
} as const;

export type PaletteVariant = LiteralValues<typeof PaletteVariant>;

// Using the radix palette definition.
// To know more about palette composition and Usage, check out their documentation:
// https://www.radix-ui.com/colors/docs/palette-composition/understanding-the-scale
export type ColorPalette = Record<PaletteVariant, { solid: AnyCssColor; alpha: AnyCssColor }>;

// Any valid css color : a hex, a color name (e.g. "red") or a css variable referencing a valid css color
export type AnyCssColor = Branded<string, 'AnyCssColor'>;

export function createDesignSystemPalette(name: string): ColorPalette {
    return {
        '1': {
            solid: `var(--theming-color-palette-${name}-1-solid)` as AnyCssColor,
            alpha: `var(--theming-color-palette-${name}-1-alpha)` as AnyCssColor
        },
        '2': {
            solid: `var(--theming-color-palette-${name}-2-solid)` as AnyCssColor,
            alpha: `var(--theming-color-palette-${name}-2-alpha)` as AnyCssColor
        },
        '3': {
            solid: `var(--theming-color-palette-${name}-3-solid)` as AnyCssColor,
            alpha: `var(--theming-color-palette-${name}-3-alpha)` as AnyCssColor
        },
        '4': {
            solid: `var(--theming-color-palette-${name}-4-solid)` as AnyCssColor,
            alpha: `var(--theming-color-palette-${name}-4-alpha)` as AnyCssColor
        },
        '5': {
            solid: `var(--theming-color-palette-${name}-5-solid)` as AnyCssColor,
            alpha: `var(--theming-color-palette-${name}-5-alpha)` as AnyCssColor
        },
        '6': {
            solid: `var(--theming-color-palette-${name}-6-solid)` as AnyCssColor,
            alpha: `var(--theming-color-palette-${name}-6-alpha)` as AnyCssColor
        },
        '7': {
            solid: `var(--theming-color-palette-${name}-7-solid)` as AnyCssColor,
            alpha: `var(--theming-color-palette-${name}-7-alpha)` as AnyCssColor
        },
        '8': {
            solid: `var(--theming-color-palette-${name}-8-solid)` as AnyCssColor,
            alpha: `var(--theming-color-palette-${name}-8-alpha)` as AnyCssColor
        },
        '9': {
            solid: `var(--theming-color-palette-${name}-9-solid)` as AnyCssColor,
            alpha: `var(--theming-color-palette-${name}-9-alpha)` as AnyCssColor
        },
        '10': {
            solid: `var(--theming-color-palette-${name}-10-solid)` as AnyCssColor,
            alpha: `var(--theming-color-palette-${name}-10-alpha)` as AnyCssColor
        },
        '11': {
            solid: `var(--theming-color-palette-${name}-11-solid)` as AnyCssColor,
            alpha: `var(--theming-color-palette-${name}-11-alpha)` as AnyCssColor
        },
        '12': {
            solid: `var(--theming-color-palette-${name}-12-solid)` as AnyCssColor,
            alpha: `var(--theming-color-palette-${name}-12-alpha)` as AnyCssColor
        }
    };
}

export const colorPaletteZero = `var(--theming-color-palette-0)` as AnyCssColor;

export const designSystemColorPalettes: Record<SharedUiColorNameV2, ColorPalette> = {
    default: createDesignSystemPalette(SharedUiColorNameV2.DEFAULT),
    primary: createDesignSystemPalette(SharedUiColorNameV2.PRIMARY),
    success: createDesignSystemPalette(SharedUiColorNameV2.SUCCESS),
    neutral: createDesignSystemPalette(SharedUiColorNameV2.NEUTRAL),
    failure: createDesignSystemPalette(SharedUiColorNameV2.FAILURE),
    info: createDesignSystemPalette(SharedUiColorNameV2.INFO),
    danger: createDesignSystemPalette(SharedUiColorNameV2.DANGER),
    'category-0': createDesignSystemPalette(SharedUiColorNameV2.CATEGORY_0),
    'category-1': createDesignSystemPalette(SharedUiColorNameV2.CATEGORY_1),
    'category-2': createDesignSystemPalette(SharedUiColorNameV2.CATEGORY_2),
    'category-3': createDesignSystemPalette(SharedUiColorNameV2.CATEGORY_3),
    'category-4': createDesignSystemPalette(SharedUiColorNameV2.CATEGORY_4),
    'category-5': createDesignSystemPalette(SharedUiColorNameV2.CATEGORY_5),
    'category-6': createDesignSystemPalette(SharedUiColorNameV2.CATEGORY_6),
    'category-7': createDesignSystemPalette(SharedUiColorNameV2.CATEGORY_7)
};

export const prettyFeelColorPalettes: Record<PrettyFeelSharedUiColorName, ColorPalette> = {
    gray: createDesignSystemPalette(PrettyFeelSharedUiColorName.GRAY),
    tomato: createDesignSystemPalette(PrettyFeelSharedUiColorName.TOMATO),
    orange: createDesignSystemPalette(PrettyFeelSharedUiColorName.ORANGE),
    amber: createDesignSystemPalette(PrettyFeelSharedUiColorName.AMBER),
    grass: createDesignSystemPalette(PrettyFeelSharedUiColorName.GRASS),
    cyan: createDesignSystemPalette(PrettyFeelSharedUiColorName.CYAN),
    blue: createDesignSystemPalette(PrettyFeelSharedUiColorName.BLUE),
    violet: createDesignSystemPalette(PrettyFeelSharedUiColorName.VIOLET),
    crimson: createDesignSystemPalette(PrettyFeelSharedUiColorName.CRIMSON)
};

export function getCategoryColorPalette(categoryIndex: number): ColorPalette {
    return designSystemColorPalettes[`category-${categoryIndex % 8}` as SharedUiColorNameV2];
}
